import React, { useState, useRef, useCallback, useEffect, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { optimizeImageUrl, getResponsiveImagePreset } from '../utils/ImageUtils';
import { useMovieDetails } from '../hooks/useMovieDetails';
import BkfcPlayer from './player/BkfcPlayer';
import analytics from '../services/analytics';
import ErrorBoundary from './common/ErrorBoundary';
import { useAuthContext } from '../contexts/AuthContext';
import spotlightService from '../services/SpotlightService';
import userDataService from '../services/UserDataService';
import cmsService, { extractGigcasterIdFromChannel } from '../services/CMSService';
import MetaTags from './SEO/MetaTags';
import SocialShare from './SEO/SocialShare';
import { generateMovieSEO, generateStructuredData } from '../utils/SEOUtils';
import {
  formatChannelEventDate,
  getChannelEventDateValue,
  getCustomFieldValue,
} from '../utils/channelDateUtils';
import { useWatchButtonState } from '../hooks/useWatchButtonState';
import { useWatchPointAutosave } from '../hooks/useWatchPointAutosave';
import WatchButton from './common/WatchButton';

import './WatchTransition.css';

// Same-video rule shared with mobile/TV: resume only when 0 < point < 95% of a
// known duration; at >= 95% it is Watch Again and starts from 0.
export const resumePositionFor = (point, duration) => {
  const p = Number(point);
  if (!(p > 0)) {
    return 0;
  }
  const d = Number(duration);
  return d > 0 && p >= 0.95 * d ? 0 : p;
};

const MoviePDP = ({ movieId, onRelatedContentSelect, isWatchMode: propIsWatchMode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, isLoading: authLoading } = useAuthContext();

  // Add mount/unmount logging and cleanup
  useEffect(() => {
    return () => {
      // Clear caches and refresh resumptions data when leaving the page
      // Clear the resumptions cache to force fresh API calls
      spotlightService.clearResumptionsCache();

      // Clear video watch point cache to force fresh API calls
      spotlightService.clearVideoWatchPointCache();

      // Refresh the appload resumptions data
      userDataService.refreshResumptionsData().catch(error => {
        console.error('🔥 MoviePDP: Error refreshing resumptions data:', error);
      });
    };
  }, []);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPreloader, setShowPreloader] = useState(false);
  const [playerLoaded, setPlayerLoaded] = useState(false);

  // Resume position: undefined = not determined yet. The player only mounts once
  // it is a number, so `startAt` is known when the stream starts loading.
  const [resumeAt, setResumeAt] = useState(undefined);
  const videoElementRef = useRef(null);

  // Detect if we're in watch mode (either from prop or URL)
  const isWatchMode = propIsWatchMode || location.pathname.startsWith('/watch/');

  // Overlay controls state for watch mode
  const [showOverlay, setShowOverlay] = useState(true);
  const overlayTimeoutRef = useRef(null);
  const playerContainerRef = useRef(null);

  // Handle mouse movement and interaction
  useEffect(() => {
    if (!isWatchMode) {
      return;
    }

    const resetOverlayTimer = () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }

      setShowOverlay(true);
      overlayTimeoutRef.current = setTimeout(() => {
        setShowOverlay(false);
      }, 3000); // Hide after 3 seconds of inactivity
    };

    const handleUserActivity = () => {
      resetOverlayTimer();
    };

    // Add event listeners
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity);

    // Initial timer
    resetOverlayTimer();

    return () => {
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('mousedown', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('touchstart', handleUserActivity);
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, [isWatchMode]); // Only depend on isWatchMode

  // Use the new custom hook for movie data fetching
  const { movie, relatedContent, loading, error, retry } = useMovieDetails(movieId);
  const [resolvedGigcasterId, setResolvedGigcasterId] = useState(null);
  const [, setEventMetaVersion] = useState(0);

  const playerRef = useRef(null);
  // Cleanup for the analytics video/QoE binding attached after the player loads.
  const videoBindingRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const resolveEventMeta = async () => {
      if (!movie) {
        if (isMounted) {
          setResolvedGigcasterId(null);
        }
        return;
      }

      const directId = extractGigcasterIdFromChannel(movie);
      let gigcasterId = directId;

      if (!gigcasterId) {
        const resolvedIds = await cmsService.resolveGigcasterIdsForChannel(movie);
        gigcasterId = resolvedIds[0] || null;
      }

      if (!isMounted) return;
      setResolvedGigcasterId(gigcasterId);

      if (gigcasterId) {
        await cmsService.preloadSlimEventData([gigcasterId]);
        if (isMounted) {
          setEventMetaVersion(v => v + 1);
        }
      }
    };

    resolveEventMeta();

    return () => {
      isMounted = false;
    };
  }, [movie?._id, movie?.slug, movie?.permalink]);

  const slimEventData = resolvedGigcasterId
    ? cmsService.getSlimEventData(resolvedGigcasterId)
    : null;
  const movieWatchState = useWatchButtonState(movie, slimEventData);
  const overlayEventDateLabel = formatChannelEventDate(
    getChannelEventDateValue(movie, slimEventData),
  );
  const overlayLocationLabel =
    slimEventData?.location ||
    slimEventData?.city_state ||
    movie?.location ||
    getCustomFieldValue(movie?.custom_fields, 'location') ||
    getCustomFieldValue(movie?.custom_fields, 'city_state') ||
    slimEventData?.venue_name ||
    slimEventData?.venue ||
    movie?.venue_name ||
    getCustomFieldValue(movie?.custom_fields, 'venue_name') ||
    getCustomFieldValue(movie?.custom_fields, 'venue') ||
    '';

  // Determine where playback starts. This is the only place the saved point is
  // read; the player applies it via `startAt` once the stream is attached.
  const resumeVideoId = movie?.video?._id;
  useEffect(() => {
    setResumeAt(undefined);
    if (!isWatchMode || !resumeVideoId || authLoading) {
      return undefined;
    }
    if (!isAuthenticated || !user) {
      setResumeAt(0);
      return undefined;
    }

    let cancelled = false;
    const determine = async () => {
      const videoId = resumeVideoId;
      const matches = item => item.video_id === videoId || item._id === videoId;
      const watchAgainMatch = userDataService.getWatchAgain()?.find(matches);
      const continueWatchingMatch = userDataService.getContinueWatching()?.find(matches);

      if (watchAgainMatch && !continueWatchingMatch) {
        return 0;
      }
      if (continueWatchingMatch?.point > 0) {
        return resumePositionFor(continueWatchingMatch.point, continueWatchingMatch.duration);
      }
      const response = await spotlightService.getVideoWatchPoint(videoId);
      const doc = response?.data || response?.result;
      return resumePositionFor(
        doc?.point ?? response?.point,
        doc?.duration || movie?.video?.duration,
      );
    };

    determine()
      .catch(error => {
        console.error('MoviePDP: Error loading watch point:', error);
        return 0;
      })
      .then(position => {
        if (!cancelled) {
          setResumeAt(position);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatchMode, resumeVideoId, authLoading, isAuthenticated, user]);

  // Resume-point saves for watch mode. Cadence (30s), live-gating, dedupe and
  // the tab-hide/page-hide keepalive save all come from useWatchPointAutosave,
  // shared with SeriesWatchPage; this page only feeds it pause/ended.
  const watchVideoId = movie?.video?._id;
  const saveNow = useWatchPointAutosave({
    enabled: Boolean(isWatchMode && isAuthenticated && user && watchVideoId),
    isLive: movieWatchState.isLive,
    getCurrentTime: () => videoElementRef.current?.currentTime || 0,
    onSave: (time, opts = {}) =>
      spotlightService
        .saveVideoWatchPoint(watchVideoId, time, {
          channelId: movie?._id,
          parentChannelId: movie?.parent_channel_id || movie?.parentChannelId,
          keepalive: opts.keepalive,
        })
        .catch(error => {
          console.error('🎬 MoviePDP: Error saving watch point:', error);
        }),
  });

  useEffect(() => {
    const videoElement = videoElementRef.current;
    if (!playerLoaded || !videoElement) {
      return undefined;
    }
    const handlePause = () => saveNow(videoElement.currentTime);
    // Save the full duration on end so a finished title stays in Continue
    // Watching / Watch Again instead of dropping out (position 0 is discarded
    // server-side). Skip the save if duration isn't a usable number.
    const handleEnded = () => {
      const duration = videoElement.duration;
      if (duration && isFinite(duration)) {
        saveNow(duration);
      }
    };
    videoElement.addEventListener('pause', handlePause);
    videoElement.addEventListener('ended', handleEnded);
    return () => {
      videoElement.removeEventListener('pause', handlePause);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [playerLoaded, saveNow]);

  // Auto-play when in watch mode - wait for movie data to load
  useEffect(() => {
    // Only proceed if we're in watch mode, have movie data, and not already playing
    if (isWatchMode && movie && movie.video?._id && !isPlaying && !loading) {
      setIsPlaying(true);
    } else if (isWatchMode && !movie && !loading) {
      console.warn('⚠️ MoviePDP: Watch mode active but no movie data after loading completed');
    }
  }, [isWatchMode, movie, isPlaying, loading]); // Include loading state

  // The player reports ready with the real <video> element and its video.js
  // instance — no mount race, no polling, no reaching through internals.
  const handlePlayerReady = useCallback(
    ({ video, player }) => {
      videoElementRef.current = video;

      // Wire real engagement + QoE analytics to the loaded player.
      if (videoBindingRef.current) {
        videoBindingRef.current();
      }
      videoBindingRef.current = analytics.bindPlayer(
        player,
        {
          content_id: movie?._id || movie?.video?._id,
          content_title: movie?.title,
          content_type: 'movie',
          duration: movie?.video?.duration || movie?.duration,
          entitlement: movie?.entitlement,
        },
        { targetSelector: '#pdp-player' },
      );

      setPlayerLoaded(true);
      setShowPreloader(false);
    },
    [movie],
  );

  // Drop the analytics binding when playback stops or the page goes away.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    return () => {
      if (videoBindingRef.current) {
        videoBindingRef.current();
        videoBindingRef.current = null;
      }
      videoElementRef.current = null;
      setPlayerLoaded(false);
    };
  }, [isPlaying, movie]);

  // Memoized event handlers
  const handlePlayClick = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handleBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAddToWatchlist = useCallback(() => {
    // This would be implemented with actual watchlist functionality
    // Added to watchlist
    // Show a toast or notification
    alert(`Added "${movie?.title}" to your watchlist`);
  }, [movie?.title]);

  // Hero image with optimization
  const getHeroImage = useCallback(() => {
    if (!movie) {
      return null;
    }

    // `spotlight_poster` (CMS "Key Art") is the 2:3 portrait asset — landscape
    // hero reads `poster` first.
    const heroImageUrl =
      movie.poster ||
      movie.theme?.image?.poster ||
      movie.spotlight_poster ||
      movie.theme?.image?.spotlight_poster;

    if (!heroImageUrl) {
      return null;
    }

    return optimizeImageUrl(heroImageUrl, getResponsiveImagePreset('CHANNEL_HERO'));
  }, [movie]);

  // Navigation buttons component for reuse across states
  const NavigationButtons = ({ isOverlay = false }) => {
    const navButtonClass = isOverlay
      ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/20 bg-black/35 text-white/90 hover:text-accent hover:border-accent/50 transition-colors duration-200 backdrop-blur-sm font-display uppercase tracking-wide text-xs md:text-sm'
      : 'flex items-center text-white hover:text-accent transition-colors font-body';

    return (
      <div className={`flex items-center space-x-4 ${isOverlay ? 'mb-0' : 'mb-4'}`}>
        <button onClick={handleBackClick} className={navButtonClass}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Back
        </button>
        {!isWatchMode && (
          <button onClick={() => navigate('/')} className={navButtonClass}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            Home
          </button>
        )}
      </div>
    );
  };

  const logo = `${window.ASSETS_BASE_URL || window.location.origin}/images/BKFC.png`;
  // Preloader component with BKFC logo
  const Preloader = () => (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
      <div className="text-center">
        {/* BKFC Logo with growing animation */}
        <div className="mb-8">
          <img src={logo} alt="BKFC" className="h-20 md:h-24 mx-auto preloader-logo" />
        </div>

        {/* Enhanced Progress Spinner */}
        <div className="relative w-20 h-20 mx-auto mb-6">
          {/* Outer ring */}
          <div className="absolute inset-0 border-4 border-gray-800 rounded-full"></div>
          {/* Animated ring */}
          <div className="absolute inset-0 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
          {/* Inner glow effect */}
          <div
            className="absolute inset-2 border-2 border-accent/30 border-t-transparent rounded-full animate-spin"
            style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}
          ></div>
        </div>

        {/* Loading Text with pulse */}
        <p className="text-lg text-white/80 animate-pulse font-body">
          Loading your movie experience...
        </p>

        {/* Progress dots */}
        <div className="flex justify-center mt-4 space-x-2">
          <div
            className="w-2 h-2 bg-accent rounded-full animate-bounce"
            style={{ animationDelay: '0ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-accent rounded-full animate-bounce"
            style={{ animationDelay: '150ms' }}
          ></div>
          <div
            className="w-2 h-2 bg-accent rounded-full animate-bounce"
            style={{ animationDelay: '300ms' }}
          ></div>
        </div>
      </div>
    </div>
  );

  // Watch point status indicator
  const WatchPointIndicator = () => {
    // Silent implementation - no visual indicators
    return null;
  };

  // Overlay controls for watch mode
  const OverlayControls = () => (
    <div
      className={`absolute top-0 left-0 right-0 bottom-0 z-50 transition-opacity duration-300 pointer-events-none ${
        showOverlay ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Top Bar with Navigation and Movie Info */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/85 via-black/55 to-transparent p-6 pointer-events-auto backdrop-blur-[2px]">
        <NavigationButtons isOverlay={true} />
        {movie && (
          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-display uppercase tracking-wide text-white mb-2">
              {movie.title}
            </h1>
            {(() => {
              const metaItems = [
                overlayEventDateLabel && <span key="date">{overlayEventDateLabel}</span>,
                overlayLocationLabel && <span key="location">{overlayLocationLabel}</span>,
                movie.rating && (
                  <span
                    key="rating"
                    className="bg-accent px-2 py-0.5 rounded text-primary font-display uppercase tracking-wide text-xs"
                  >
                    {movie.rating}
                  </span>
                ),
              ].filter(Boolean);
              if (!metaItems.length) {
                return null;
              }
              return (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/80 text-sm font-body">
                  {metaItems.map((item, index) => (
                    <React.Fragment key={item.key}>
                      {index > 0 && (
                        <span className="text-accent/80" aria-hidden="true">
                          •
                        </span>
                      )}
                      {item}
                    </React.Fragment>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    // In watch mode, let the player handle loading state
    if (isWatchMode) {
      return (
        <div className="watch-mode" style={{ width: '100vw', height: '100vh' }}>
          <div className="relative w-full h-full">
            {/* Let the player handle its own loading state */}
          </div>
        </div>
      );
    }

    // Regular PDP skeleton loading state
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          {/* Skeleton loading state */}
          <div className="animate-pulse">
            <div className="w-full mb-8 bg-gray-700 rounded-lg h-96"></div>
            <div className="w-3/4 h-10 mb-4 bg-gray-700 rounded"></div>
            <div className="w-1/4 h-4 mb-6 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-full h-4 mb-2 bg-gray-700 rounded"></div>
            <div className="w-5/6 h-4 mb-8 bg-gray-700 rounded"></div>

            <div className="flex mb-8 space-x-4">
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
              <div className="w-32 h-10 bg-gray-700 rounded"></div>
            </div>

            <div className="w-48 h-6 mb-4 bg-gray-700 rounded"></div>
            <div className="flex space-x-4 overflow-x-auto">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex-shrink-0 w-64">
                  <div className="mb-2 bg-gray-700 rounded-lg h-36"></div>
                  <div className="w-3/4 h-4 mb-1 bg-gray-700 rounded"></div>
                  <div className="w-1/2 h-3 bg-gray-700 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="p-6 text-center border border-accent rounded-lg bg-accent/10">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12 mx-auto mb-4 text-accent"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mb-2 text-xl font-bold text-white">Error Loading Movie</h2>
            <p className="mb-4 text-white/70">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleBackClick}
                className="px-6 py-2 font-medium text-white rounded-full bg-accent hover:bg-accent-hover"
              >
                Go Back
              </button>
              <button
                onClick={retry}
                className="px-6 py-2 font-medium text-white rounded-full bg-secondary hover:bg-[#2a2a2a]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen movie-pdp bg-primary">
        <div className="container px-4 py-8 mx-auto">
          <NavigationButtons />

          <div className="py-12 text-center">
            <h2 className="mb-2 text-xl font-bold text-white">Movie Not Found</h2>
            <p className="mb-4 text-white/70">The requested movie could not be found.</p>
            <button
              onClick={handleBackClick}
              className="px-6 py-2 font-medium text-white rounded-full bg-accent hover:bg-accent-hover"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const heroImage = getHeroImage();
  const title = movie.title || 'Untitled Movie';
  const description = movie.description || 'No description available';
  const initialDuration = Math.ceil(Number(movie.video?.duration));
  let duration = 'Unknown Duration';
  if (initialDuration) {
    const h = Math.floor(initialDuration / 3600);
    const m = Math.floor((initialDuration % 3600) / 60);
    const s = initialDuration % 60;
    if (h) {
      duration = `${h}h ${m}m ${s}s`;
    } else {
      duration = `${m}m ${s}s`;
    }
  }
  const releaseYear = movie.year || movie.video?.year || 'Unknown year';
  const genres = movie.genres?.join(', ') || 'Unknown genre';
  const rating = movie.rating || movie.video?.rating || 'Not rated';
  const actors = movie.actors || movie.video?.actors || [];
  const directors = movie.directors || movie.video?.directors || [];
  const writers = movie.writers || movie.video?.writers || [];

  // Watch mode layout - Simplified structure with restored functionality
  if (isWatchMode) {
    return (
      <div className="watch-container" ref={playerContainerRef}>
        {/* Preloader */}
        {showPreloader && <Preloader />}

        {/* Video Player */}
        <div id="pdp-player" ref={playerRef}>
          {isPlaying && movie?.video?._id && resumeAt !== undefined && (
            <BkfcPlayer
              videoId={movie.video._id}
              autostart
              controls
              muted={false}
              startAt={resumeAt}
              onReady={handlePlayerReady}
            />
          )}
        </div>

        {/* Watch Point Status Indicator */}
        <WatchPointIndicator />

        {/* Overlay Controls */}
        {!showPreloader && <OverlayControls />}
      </div>
    );
  }

  // Regular PDP layout
  const seoData = generateMovieSEO(movie, channelData);
  const structuredData = generateStructuredData(movie, 'movie');

  return (
    <div className={`min-h-screen movie-pdp bg-primary ${isWatchMode ? 'watch-mode' : ''}`}>
      {/* SEO Meta Tags */}
      <MetaTags {...seoData} />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <div className="container px-4 py-8 mx-auto">
        <NavigationButtons />

        {isPlaying ? (
          <div className={`mb-8 ${isWatchMode ? 'watch-mode-player' : ''}`}>
            <div
              id="pdp-player"
              ref={playerRef}
              className={`w-full bg-black rounded-lg overflow-hidden ${
                isWatchMode ? 'aspect-video min-h-[80vh]' : 'aspect-video min-h-[400px]'
              }`}
            >
              {movie?.video?._id && (
                <BkfcPlayer
                  videoId={movie.video._id}
                  autostart
                  controls
                  muted={false}
                  onReady={handlePlayerReady}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="relative w-full h-[40vh] md:h-[60vh] rounded-b-2xl overflow-hidden mb-8">
            <img
              src={`${heroImage}?w=1200&h=675&fit=crop`}
              alt={title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/90 via-[#1a1a1a]/60 to-transparent" />
            <div className="relative z-10 flex flex-col justify-end h-full p-6">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{title}</h1>
              <div className="flex flex-wrap items-center text-white/80 text-base mb-4 space-x-4">
                <span>{releaseYear}</span>
                <span>•</span>
                <span>{duration}</span>
                <span>•</span>
                <span>{genres}</span>
                {rating && (
                  <>
                    <span>•</span>
                    <span className="bg-accent/20 px-2 py-0.5 rounded text-accent">{rating}</span>
                  </>
                )}
              </div>
              <WatchButton
                watchState={movieWatchState}
                onClick={handlePlayClick}
                onWatchIn={() => {}}
                className="rounded-full shadow-lg w-max"
                size="lg"
              />
            </div>
          </div>
        )}

        {/* Content sections - hidden in watch mode but data is preserved */}
        {!isWatchMode && (
          <>
            <div className="flex flex-wrap items-center text-white/80 text-base mb-4 space-x-4">
              <span>{releaseYear}</span>
              <span>•</span>
              <span>{duration}</span>
              <span>•</span>
              <span>{genres}</span>
              <span>•</span>
              <span>Audio: English</span>
              <span>•</span>
              <span>Subtitles: English</span>
            </div>
            <div className="font-bold text-white mb-2">{description}</div>

            {/* Social Sharing */}
            <div className="mb-6">
              <SocialShare
                url={seoData.url}
                title={seoData.title}
                description={seoData.description}
                image={seoData.image}
                className="justify-start"
              />
            </div>

            <ErrorBoundary fallback="Failed to load cast and crew information">
              <Suspense fallback={<LoadingSpinner message="Loading cast and crew..." />}>
                <CastCrew
                  actors={actors}
                  directors={directors}
                  writers={writers}
                  className="mb-8"
                />
              </Suspense>
            </ErrorBoundary>

            <div className="mb-8">
              <ErrorBoundary fallback="Failed to load related content">
                <Suspense fallback={<LoadingSpinner message="Loading related content..." />}>
                  {relatedContent.length > 0 && (
                    <RelatedContent items={relatedContent} onSelect={onRelatedContentSelect} />
                  )}
                </Suspense>
              </ErrorBoundary>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MoviePDP;
